import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as Common from "../commons/common";

import DataTable from "../components/DataTable";
import RentRegisterNotice from "../components/RentRegisterNotice";
import moment from "moment";
import Highlighter from "react-highlight-words";

type DataIndex = keyof Types.RentNoticeDto;

function NoticeList() {
    const alert = useAlert();

    const [rentNotices, setRentNotices] = useState<Array<Types.RentNoticeDto>>();
    const [currentNotice, setCurrentNotice] = useState<Types.RentNoticeDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteNotice, setOpenDeleteNotice] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    useEffect(() => {
        requestGetRentNotices();
    }, []);

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.RentNoticeDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        {String.search}
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        {String.reset}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        {String.filter}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        {String.close}
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.RentNoticeDto> = [
        {
            align: "center",
            title: String.id,
            dataIndex: "rentNoticeId",
            key: "rentNoticeId",
            ...getColumnSearchProps("rentNoticeId"),
        },
        {
            align: "left",
            title: String.title,
            dataIndex: "title",
            key: "title",
            ...getColumnSearchProps("title"),
        },
        {
            align: "center",
            title: "카테고리",
            dataIndex: "noticeType",
            key: "noticeType",
            render: (_, { noticeType }) => {
                return (
                    <span>
                        {noticeType === "NOTICE"
                            ? "공지사항"
                            : noticeType === "EVENT"
                            ? "이벤트"
                            : noticeType === "EVENT_ANNOUNCEMENT"
                            ? "이벤트 발표"
                            : String.dash}
                    </span>
                );
            },
        },
        {
            align: "center",
            title: "이벤트 상태",
            dataIndex: "noticeStatus",
            key: "noticeStatus",
            render: (_, { noticeStatus }) => {
                return <span>{noticeStatus === "EVENT_FINISHED" ? "종료" : noticeStatus === "EVENT_IN_PROGRESS" ? "진행" : String.dash}</span>;
            },
        },
        {
            align: "center",
            title: String.fixed,
            dataIndex: "fixed",
            key: "fixed",
            render: (_, { fixed }) => {
                return <span>{fixed === true ? "○" : "✕"}</span>;
            },
        },
        {
            align: "center",
            title: String.hits,
            dataIndex: "hits",
            render: (_, { hits }) => hits || String.dash,
        },
        {
            align: "center",
            title: String.registeredDate,
            dataIndex: "registeredDate",
            key: "registeredDate",
            render: (_, { registeredDate }) => (registeredDate ? moment(registeredDate).format(Common.FORMAT_DATE) : String.dash),
        },
        {
            align: "center",
            title: " ",
            dataIndex: "action",
            key: "action",
            render: (_, row) => {
                return (
                    <>
                        <Button
                            shape="round"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setCurrentNotice(row);
                                setEditMode("update");
                            }}
                        >
                            {String.update}
                        </Button>
                        <Button
                            shape="round"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                setCurrentNotice(row);
                                setOpenDeleteNotice(true);
                            }}
                        >
                            {String.remove}
                        </Button>
                    </>
                );
            },
        },
    ];

    //get notices
    const requestAxiosGetRentNotices = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_NOTICE_LIST_URL);
        console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetRentNotices,
        error: errorGetRentNotices,
        data: resultGetRentNotices,
        execute: requestGetRentNotices,
    } = useAsyncAxios(requestAxiosGetRentNotices);

    useEffect(() => {
        if (resultGetRentNotices === null) return;
        console.log("resultGetRentNotices", resultGetRentNotices);

        setRentNotices(resultGetRentNotices.rentNotices);
    }, [resultGetRentNotices]);

    useEffect(() => {
        if (errorGetRentNotices === null) return;

        alert.setAlert(errorGetRentNotices.response.data.code, "Error", errorGetRentNotices.response.data.message);
    }, [errorGetRentNotices]);

    //delete notice
    const requestAxiosRemoveNotice = async (notice: Types.RentNoticeDto) => {
        const response = await utilAxiosWithAuth().delete(Request.RENT_NOTICE_URL + "/" + notice?.rentNoticeId);
        return response.data;
    };

    const {
        loading: loadingRemoveNotice,
        error: errorRemoveNotice,
        data: resultRemoveNotice,
        execute: requestRemoveRentNotices,
    } = useAsyncAxios(requestAxiosRemoveNotice);

    useEffect(() => {
        if (resultRemoveNotice === null) return;
        console.log("resultRemoveNotice", resultRemoveNotice);

        alert.setAlert(AlertType.SUCCESS, "공지사항 삭제 성공", "공지사항 삭제를 완료하였습니다.");

        requestGetRentNotices();
    }, [resultRemoveNotice]);

    useEffect(() => {
        if (errorRemoveNotice === null) return;

        alert.setAlert(errorRemoveNotice.response.data.code, "Error", errorRemoveNotice.response.data.message);
    }, [errorGetRentNotices]);

    const onRow = (value: Types.RentNoticeDto) => {
        setEditMode("none");
        setCurrentNotice(value);
    };

    const onRegisterNotice = () => {
        requestGetRentNotices();
        setEditMode("none");
    };

    const handleDeleteNoticeOk = () => {
        setOpenDeleteNotice(false);
        requestRemoveRentNotices(currentNotice);
    };

    const handleDeleteNoticeCancel = () => {
        setOpenDeleteNotice(false);
    };

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    return (
        <>
            <Row justify={"space-between"}>
                <Col span={6}>
                    <Divider orientation="left">
                        <span>공지/이벤트</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            setCurrentNotice(undefined);
                            setEditMode("add");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                </Space>
            </Row>
            <DataTable
                rowKey={(row: Types.RentNoticeDto) => row.rentNoticeId}
                loading={loadingGetRentNotices}
                hasScroll={false}
                columns={columns}
                dataSource={rentNotices}
                onRow={onRow}
                tableSize={30}
            />
            {editMode !== "none" && <RentRegisterNotice editMode={editMode} rentNotice={currentNotice} onRegisterRentNotice={onRegisterNotice} />}
            {openDeleteNotice && (
                <Modal open={openDeleteNotice} onOk={handleDeleteNoticeOk} onCancel={handleDeleteNoticeCancel}>
                    {currentNotice?.title} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default NoticeList;
